import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

import { ReactComponent as CaretDown } from 'assets/icons/caret-down.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search-icon.svg'

import { Divider } from './Divider'
import { Icon } from './Icon'

const getValueKey = (val: any): string => {
  if (val == null) return String(val)
  if (typeof val === 'object') return val.id ?? val.value ?? JSON.stringify(val)
  return String(val)
}

const getLabelText = (node: any): string => {
  if (node == null) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getLabelText).join('')
  if (React.isValidElement(node)) {
    const props = node.props as { children?: any }
    return getLabelText(props.children)
  }
  return ''
}

interface Option {
  value: any
  labelText: string
  rawLabel: React.ReactNode
  disabled?: boolean
}

interface SingleSelectChangeEvent {
  detail: { value: any }
  target: { value: any }
}

interface SingleSelectProps {
  value?: any
  onChange?: (event: SingleSelectChangeEvent) => void
  children: React.ReactNode
  placeholder?: string
  tooltipText?: string
  labelConfig?: { text?: string }
  error?: boolean
  disabled?: boolean
  helpText?: string
  id?: string
  required?: boolean
  hideSearch?: boolean
  className?: string
  size?: 's' | 'm'
  onSearchValueChange?: (event: { detail: string }) => void
}

const extractOptions = (children: React.ReactNode): Option[] =>
  React.Children.toArray(children).map((child: any) => {
    const val = child.props.value
    let rawLabel: React.ReactNode = child.props.children

    if (React.isValidElement(rawLabel)) {
      const props = rawLabel.props as { slot?: string; children?: React.ReactNode }
      if (props.slot === 'label') rawLabel = props.children
    }

    return {
      value: val,
      labelText: getLabelText(rawLabel),
      rawLabel,
      disabled: child.props.disabled,
    }
  })

export const SingleSelect: React.FC<SingleSelectProps> = ({
  value,
  onChange,
  children,
  placeholder = 'Select',
  error = false,
  disabled = false,
  helpText,
  tooltipText,
  labelConfig,
  id,
  required = false,
  className,
  hideSearch = true,
  size = 'm',
  onSearchValueChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options = extractOptions(children)
  // Key-based lookup so object values (e.g. Country) match correctly
  const selectedOption = options.find(opt => getValueKey(opt.value) === getValueKey(value))
  const filteredOptions = options.filter(opt => opt.labelText.toLowerCase().includes(searchQuery.toLowerCase()))

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleItemClick = (option: Option) => {
    if (option.disabled) return
    onChange?.({ detail: { value: option.value }, target: { value: option.value } })
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchQuery(val)
    onSearchValueChange?.({ detail: val })
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    onSearchValueChange?.({ detail: '' })
  }

  return (
    <div className={clsx('relative flex flex-col gap-2', className)} ref={dropdownRef}>
      {labelConfig?.text && (
        <label htmlFor={id} className="flex items-center gap-2 text-base leading-6 text-[#505050]">
          <span className="tracking-wide">{labelConfig.text}</span>
          {!required && <span className="text-muted-foreground">(Optional)</span>}
          {tooltipText && (
            <div className="group relative">
              <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-3 py-1.5 text-xs text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                {tooltipText}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-foreground" />
              </div>
            </div>
          )}
        </label>
      )}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        className={clsx(
          'w-full flex gap-1 rounded-lg items-center text-[#505050] border border-solid border-[#a2a9b0] px-[10px] text-sm transition-colors',
          size === 's' ? 'py-1.5' : 'py-2.5',
          'hover:border-[#697077] hover:bg-[#f0f2F5] focus:border-black focus:text-[6E6E6E]',
          error &&
            'border-[#DE0000] text-foreground focus:ring-[#DE0000] hover:border-[#DE0000] hover:text-[#000] hover:bg-[#D4D4D4]',
          !error && !disabled && 'hover:border-black hover:text-black hover:bg-[#D4D4D4]',
          disabled && 'cursor-not-allowed opacity-50 border-[#9B9B9B] bg-[#F1F1F1] text-[#9B9B9B]',
        )}
      >
        {/* Always render plain text in the trigger — rawLabel may contain images */}
        <span
          className={clsx(
            'grow text-nowrap w-full text-left overflow-hidden text-ellipsis',
            selectedOption ? 'text-[#121619]' : 'text-[#5f5f5f]',
          )}
        >
          {selectedOption ? selectedOption.labelText : placeholder}
        </span>
        <Icon icon={CaretDown} className={clsx('transition-transform', isOpen && '-rotate-180')} color="#8b919a" />
      </button>

      {helpText && (
        <div className={clsx('text-xs leading-5', error ? 'text-[#DE0000]' : 'text-[#505050]')}>{helpText}</div>
      )}

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 w-full rounded-md bg-white z-[3333]"
          style={{ boxShadow: '0px 1px 4px 0px rgba(53, 59, 65, 0.05), 0px 4px 16px 0px rgba(53, 59, 65, 0.12)' }}
        >
          {!hideSearch && (
            <>
              <div className="flex items-center gap-2 p-2">
                <Icon icon={SearchIcon} className="[&_path]:text-[#505050]" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="flex-1 bg-transparent text-14 outline-none text-[#505050] placeholder:text-[#b1b0b0]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-[12px] border-none bg-transparent cursor-pointer p-0"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <Icon icon={CloseIcon} aria-label="Clear icon" color="#8b919a" />
                  </button>
                )}
              </div>
              <Divider />
            </>
          )}
          <div className="max-h-[311px] overflow-y-auto p-2 flex flex-col gap-1">
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                disabled={option.disabled}
                onClick={() => handleItemClick(option)}
                className={clsx(
                  'w-full text-[#191919] font-normal px-2 py-1 text-left text-sm leading-6 hover:bg-[#f0f2f5] transition-colors',
                  getValueKey(selectedOption?.value) === getValueKey(option.value) && '!font-medium !bg-[#eee8ff]',
                  option.disabled && 'opacity-50 cursor-not-allowed',
                )}
              >
                {/* rawLabel renders flags/icons in the dropdown correctly */}
                {option.rawLabel}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-6 py-4 text-center text-muted-foreground">No items found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SingleSelect
